/**
 * Connect Device — restore URL on reload (e.g. when internet reconnects).
 * Saves the current connect-device hash to localStorage (survives WebView reload;
 * sessionStorage is often cleared when the WebView reloads).
 */

const CONNECT_DEVICE_RESTORE_KEY = "circadia_connect_device_restore";

/**
 * Call once at app entry (main.tsx before rendering the router).
 * If we're not already on connect-device but have a saved connect-device URL, redirect to it.
 * @returns true if a redirect was performed (caller should not render the app)
 */
export function restoreConnectDeviceIfNeeded(): boolean {
  if (typeof window === "undefined") return false;
  const hash = window.location.hash || "";
  const pathname = window.location.pathname || "";

  // Already on connect-device — nothing to restore
  if (hash.includes("connect-device") || pathname.includes("connect-device"))
    return false;

  const saved = localStorage.getItem(CONNECT_DEVICE_RESTORE_KEY);
  if (!saved || !saved.includes("connect-device")) return false;

  localStorage.removeItem(CONNECT_DEVICE_RESTORE_KEY);
  const target = saved.startsWith("#") ? saved : "#" + saved;
  window.location.replace(window.location.origin + pathname + target);
  return true;
}

/**
 * Call from Connect Device page in a useEffect when step/deviceId/roomName change.
 * Never overwrites a saved URL that has a higher step — the portal pre-saves
 * step=2 before opening, and a Vite HMR reload must not clobber that with step=1.
 */
export function saveConnectDeviceRestoreUrl(): void {
  if (typeof window === "undefined") return;
  const hash = window.location.hash || "";
  if (!hash.includes("connect-device")) return;

  const existing = localStorage.getItem(CONNECT_DEVICE_RESTORE_KEY);
  if (existing) {
    const existingStep = parseInt(existing.match(/step=(\d+)/)?.[1] ?? "0", 10);
    const newStep = parseInt(hash.match(/step=(\d+)/)?.[1] ?? "0", 10);
    if (newStep < existingStep) return;
  }
  localStorage.setItem(CONNECT_DEVICE_RESTORE_KEY, hash);
}

/**
 * Call when the user leaves the Connect Device flow (e.g. handleFinish).
 */
export function clearConnectDeviceRestoreUrl(): void {
  if (typeof window === "undefined") return;
  localStorage.removeItem(CONNECT_DEVICE_RESTORE_KEY);
}

/**
 * Peek at the saved connect-device URL without removing it (for redirects).
 */
export function getSavedConnectDeviceUrl(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const saved = localStorage.getItem(CONNECT_DEVICE_RESTORE_KEY);
    if (!saved || !saved.includes("connect-device")) return null;
    const path = saved.startsWith("#") ? saved.slice(1) : saved;
    return path || null;
  } catch {
    return null;
  }
}

/**
 * Get the saved connect-device URL for redirect (e.g. after login).
 * Returns the path + search (e.g. "/connect-device?step=0") or null, and removes it from storage.
 */
export function takeSavedConnectDeviceUrl(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const saved = localStorage.getItem(CONNECT_DEVICE_RESTORE_KEY);
    if (!saved || !saved.includes("connect-device")) return null;
    localStorage.removeItem(CONNECT_DEVICE_RESTORE_KEY);
    const path = saved.startsWith("#") ? saved.slice(1) : saved;
    return path || null;
  } catch {
    return null;
  }
}
